/**
 * Agent · Brief (phases B and C). The organization today for the compliance
 * team: questions first, then every department with what is due, late or
 * stalled, each item named in its app's own naming and linked. Built once a
 * day by the Nudge from the signal table; no model. Phase C: compliance may
 * act on an item from here (reassign it, send a short note, put it on the
 * calendar), with a preview before the action runs.
 */
import React, { useState } from 'react';
import { api, fmtWhen } from './api.js';

const SEV = { past_due: 'text-rose-700', high: 'text-orange-700', normal: 'text-slate-500' };
const ACTS = [['reassign', 'Reassign'], ['send_note', 'Send a note'], ['create_calendar_item', 'Put on the calendar']];

function ActOnItem({ item, people }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('reassign');
  const [to, setTo] = useState('');
  const [preview, setPreview] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const params = () => (key === 'reassign' ? { to_user_id: to } : {});
  const prep = async () => {
    setBusy(true); setMsg(null); setPreview(null);
    const d = await api('/actions/preview', { method: 'POST', body: { key, signal_key: item.signal_key, params: params() } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Cannot prepare that.'); return; }
    setPreview(d);
  };
  const run = async () => {
    setBusy(true); setMsg(null);
    const d = await api('/actions/run', { method: 'POST', body: { key, signal_key: item.signal_key, params: params() } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'The action did not run.'); return; }
    setPreview(null); setMsg(`Done: ${d.label?.toLowerCase() || 'the action ran'}.`);
  };
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="text-[12px] text-[#E5601F] hover:underline">Act</button>;
  return (
    <div className="w-full mt-2 rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-[13px]" data-brief-action>
      <div className="flex flex-wrap gap-2 items-center">
        <select value={key} onChange={(e) => { setKey(e.target.value); setPreview(null); }} className="px-2 py-1 rounded-lg ring-1 ring-slate-200 bg-white text-[13px]">{ACTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        {key === 'reassign' && (
          <select value={to} onChange={(e) => setTo(e.target.value)} className="px-2 py-1 rounded-lg ring-1 ring-slate-200 bg-white text-[13px]">
            <option value="">Choose the new owner</option>
            {Object.entries(people).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
        <button type="button" onClick={prep} disabled={busy || (key === 'reassign' && !to)} className="px-3 py-1 rounded-full bg-white ring-1 ring-slate-200 text-[12.5px] disabled:opacity-50">Preview</button>
        <button type="button" onClick={() => setOpen(false)} className="px-2 py-1 text-[12.5px] text-slate-500">Close</button>
      </div>
      {preview && (
        <div className="mt-2">
          <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Before it runs · {preview.label}</p>
          <dl className="space-y-0.5">{(preview.preview?.lines || []).map(([k, v]) => <div key={k} className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">{k}</dt><dd className="whitespace-pre-wrap text-slate-800">{v}</dd></div>)}</dl>
          {preview.preview?.note && <p className="mt-1 text-[12px] text-slate-500">{preview.preview.note}</p>}
          <button type="button" onClick={run} disabled={busy} className="mt-2 px-4 py-1.5 rounded-full bg-[#E5601F] text-white text-[13px] font-medium disabled:opacity-50">Yes, do it</button>
        </div>
      )}
      {msg && <p className="mt-2 text-slate-700">{msg}</p>}
    </div>
  );
}

export default function BriefTab({ brief, people = {}, onAnswer }) {
  if (!brief) return <section className="bg-white rounded-2xl border border-slate-200 px-5 py-6 text-sm text-slate-500">No brief yet. The Nudge builds one each morning once the agent has run; Run the Nudge now on Setup builds it at once.</section>;
  return (
    <div className="space-y-6">
      <section className="bg-white rounded-2xl border border-slate-200 px-5 py-5">
        <p className="text-[11px] uppercase tracking-wide text-slate-500">Brief for {new Date(`${String(brief.date).slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}{brief.built_at ? ` · built ${fmtWhen(brief.built_at)}` : ''}</p>
        <h2 className="text-lg font-semibold text-slate-900 mt-1">{brief.headline}</h2>
        {brief.totals && (
          <dl className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
            {[['Open', brief.totals.open], ['Past due', brief.totals.past_due], ['Due soon', brief.totals.high], ['No owner', brief.totals.unowned], ['Questions', brief.totals.questions_open]].map(([l, v]) => (
              <div key={l}><dt className="text-[11px] uppercase tracking-wide text-slate-500">{l}</dt><dd className="text-slate-800 tabular-nums text-lg font-semibold">{v}</dd></div>
            ))}
          </dl>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between"><h3 className="text-sm font-semibold text-slate-800">Questions for compliance</h3><span className="text-xs text-slate-500">{(brief.questions || []).length} open</span></div>
        <div className="px-5 py-2">
          {(brief.questions || []).length === 0 ? <p className="text-sm text-slate-500 py-2">Nobody is waiting on an answer.</p> : (brief.questions || []).map((q) => (
            <div key={q.id} className="py-3 border-b border-slate-100 last:border-0">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm text-slate-800"><span className="font-medium">{q.asker_name || 'A colleague'}</span>{q.object_label ? <span className="text-slate-500"> · {q.object_label}</span> : null}{q.escalated_at ? <span className="ml-2 text-[11px] text-orange-700">escalated to the officer</span> : null}</p>
                <span className="text-xs text-slate-400">{fmtWhen(q.created_at)}</span>
              </div>
              <p className="text-sm text-slate-700 mt-1">{q.question}</p>
              {q.context && <p className="text-xs text-slate-500 mt-1 whitespace-pre-wrap">{q.context}</p>}
              {onAnswer && <button type="button" onClick={() => onAnswer(q)} className="mt-2 text-[12.5px] font-medium text-[#E5601F] hover:underline">Answer</button>}
            </div>
          ))}
        </div>
      </section>

      {(brief.departments || []).map((d) => (
        <section key={d.label} className="bg-white rounded-2xl border border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-sm font-semibold text-slate-800">{d.label}</h3>
            <span className="text-xs text-slate-500">{d.open} open{d.past_due ? `, ${d.past_due} past due` : ''}{d.people ? ` · ${d.people} ${d.people === 1 ? 'person' : 'people'}` : ''}</span>
          </div>
          <div className="px-5 py-2">
            <p className="text-xs text-slate-500 py-1">{(d.rules || []).map((r) => `${r.title}: ${r.n}`).join(' · ')}</p>
            {(d.items || []).map((it) => (
              <div key={it.signal_key} className="py-2 border-b border-slate-100 last:border-0 flex flex-wrap items-start gap-3 text-sm">
                <span className={`shrink-0 w-40 ${SEV[it.severity] || SEV.normal}`}>{it.when}</span>
                <span className="flex-1 min-w-0 text-slate-800">{it.href ? <a href={it.href} className="hover:underline">{it.label}</a> : it.label}<span className="text-slate-400"> · {it.app}</span></span>
                <span className="shrink-0 text-slate-500">{it.person_user_id ? (people[it.person_user_id] || 'Assigned') : 'No owner'}</span>
                <ActOnItem item={it} people={people} />
              </div>
            ))}
            {d.open > (d.items || []).length && <p className="text-xs text-slate-400 py-1">and {d.open - d.items.length} more</p>}
          </div>
        </section>
      ))}
    </div>
  );
}
